// Security utilities and validation functions

import { logger } from './logger';

// Input validation and sanitization
export class InputValidator {
  // Email validation with comprehensive regex
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email) && email.length <= 254;
  }

  // Password strength validation
  static validatePassword(password: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (password.length > 128) {
      errors.push('Password must be less than 128 characters');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    // Check for common weak passwords
    const commonPasswords = ['password', '123456', 'qwerty', 'admin', 'letmein'];
    if (commonPasswords.includes(password.toLowerCase())) {
      errors.push('Password is too common and easily guessable');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Phone number validation
  static isValidPhone(phone: string): boolean {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    // Check if it's between 7 and 15 digits (international standard)
    return cleaned.length >= 7 && cleaned.length <= 15;
  }

  // Sanitize HTML input to prevent XSS
  static sanitizeHtml(input: string): string {
    const div = document.createElement('div');
    div.textContent = input;
    return div.innerHTML;
  }

  // Validate and sanitize URL
  static isValidUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  }

  // SQL injection prevention for search queries
  static sanitizeSearchQuery(query: string): string {
    return query
      .replace(/['"\\;]/g, '') // Remove quotes, backslashes, semicolons
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim()
      .substring(0, 100); // Limit length
  }

  // Validate file uploads
  static validateFileUpload(file: File, allowedTypes: string[], maxSize: number): { isValid: boolean; error?: string } {
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `File type ${file.type} is not allowed. Allowed types: ${allowedTypes.join(', ')}`
      };
    }

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum allowed size of ${(maxSize / 1024 / 1024).toFixed(2)}MB`
      };
    }

    return { isValid: true };
  }
}

// Rate limiting for API calls
export class RateLimiter {
  private static requests: Map<string, number[]> = new Map();

  static isAllowed(key: string, maxRequests: number, windowMs: number): boolean {
    const now = Date.now();
    const windowStart = now - windowMs;
    
    // Get existing requests for this key
    const requests = this.requests.get(key) || [];
    
    // Filter out old requests
    const recentRequests = requests.filter(timestamp => timestamp > windowStart);
    
    // Check if limit exceeded
    if (recentRequests.length >= maxRequests) {
      logger.warn(`Rate limit exceeded for key: ${key}`, 'RateLimiter');
      return false;
    }
    
    // Add current request
    recentRequests.push(now);
    this.requests.set(key, recentRequests);
    
    return true;
  }

  static reset(key: string) {
    this.requests.delete(key);
  }
}

// Content Security Policy helpers
class CSPHelper {
  static generateNonce(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  static setCSPHeaders() {
    const nonce = this.generateNonce();
    const csp = [
      "default-src 'self'",
      `script-src 'self' 'nonce-${nonce}' data: https://apis.google.com`,
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
      "font-src 'self' https://fonts.gstatic.com",
      "img-src 'self' data: https: blob:",
      "connect-src 'self' https://*.supabase.co https://api.zeptomail.com",
      "frame-src 'self' https://www.google.com https://www.youtube.com",
      "object-src 'none'",
      "base-uri 'self'",
      "form-action 'self'"
    ].join('; ');

    // Set CSP header (this would typically be done server-side)
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Content-Security-Policy';
    meta.content = csp;
    document.head.appendChild(meta);

    return nonce;
  }
}

// Session security
export class SessionSecurity {
  private static readonly SESSION_KEY = 'app_session_security';
  private static readonly MAX_IDLE_TIME = 30 * 60 * 1000; // 30 minutes
  private static readonly MAX_SESSION_TIME = 8 * 60 * 60 * 1000; // 8 hours

  static initializeSession() {
    const sessionData = {
      startTime: Date.now(),
      lastActivity: Date.now(),
      fingerprint: this.generateFingerprint()
    };
    
    localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));
    this.startActivityTracking();
  }

  static updateActivity() {
    const sessionData = this.getSessionData();
    if (sessionData) {
      sessionData.lastActivity = Date.now();
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));
    }
  }

  static isSessionValid(): boolean {
    const sessionData = this.getSessionData();
    if (!sessionData) return false;

    const now = Date.now();
    const idleTime = now - sessionData.lastActivity;
    const totalTime = now - sessionData.startTime;

    // Check for session timeout
    if (idleTime > this.MAX_IDLE_TIME || totalTime > this.MAX_SESSION_TIME) {
      this.clearSession();
      return false;
    }

    // Check for session hijacking
    const currentFingerprint = this.generateFingerprint();
    if (currentFingerprint !== sessionData.fingerprint) {
      logger.error('Session fingerprint mismatch - possible hijacking attempt', 'SessionSecurity');
      this.clearSession();
      return false;
    }

    return true;
  }

  static clearSession() {
    localStorage.removeItem(this.SESSION_KEY);
  }

  private static getSessionData() {
    try {
      const data = localStorage.getItem(this.SESSION_KEY);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  private static generateFingerprint(): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Browser fingerprint', 2, 2);
    }
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|');

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return hash.toString(36);
  }

  private static startActivityTracking() {
    // Track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const updateActivity = () => {
      this.updateActivity();
    };

    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Check session validity every minute
    setInterval(() => {
      if (!this.isSessionValid()) {
        window.location.href = '/signin?reason=session_expired';
      }
    }, 60000);
  }
}

// Data encryption utilities
class DataEncryption {
  // Simple encryption for sensitive data in localStorage
  static encrypt(data: string, key: string): string {
    try {
      // In a real application, use a proper encryption library like crypto-js
      // This is a simple XOR cipher for demonstration
      let result = '';
      for (let i = 0; i < data.length; i++) {
        result += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      return btoa(result);
    } catch (error) {
      logger.error('Encryption failed', 'DataEncryption', { error });
      return data; // Return original data if encryption fails
    }
  }

  static decrypt(encryptedData: string, key: string): string {
    try {
      const data = atob(encryptedData);
      let result = '';
      for (let i = 0; i < data.length; i++) {
        result += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      return result;
    } catch (error) {
      logger.error('Decryption failed', 'DataEncryption', { error });
      return encryptedData; // Return encrypted data if decryption fails
    }
  }
}

// Initialize security measures
export const initializeSecurity = () => {
  // Set CSP headers
  CSPHelper.setCSPHeaders();
  
  // Initialize session security
  SessionSecurity.initializeSession();
  
  // Log security initialization
  logger.info('Security measures initialized', 'Security');
};